/**
 * Integrytas Group - Field Lineage (FLN)
 * Client-side field registry — auto-discovered, not hand-curated.
 *
 * Every <PersonalizedField> and <PersonalizedGrid> column reports its identity
 * — (page_code, component_key, field_key, label) — as it renders. The accumulated
 * set is synced to public.fln_field_source (RPC fln_sync_fields) so an admin can
 * map + describe each field's data source. This mirrors udc_hosts.ts: registration
 * is the trigger, so a new field is "covered" the moment its page renders — no
 * central list to maintain, and derived apps' fields register the same way.
 *
 * Only admins sync (the RPC also enforces this server-side); for everyone else the
 * registry stays a harmless local cache. Pushes are debounced and never wipe the
 * server set, so partial discovery (fields appear as pages are visited) is safe.
 */

import { useEffect } from 'react';
import { apiDb } from './api_client';
import type { AuthSession } from '../types/auth';

export interface FlnFieldReg {
  page_code:     string;
  component_key: string;
  field_key:     string;
  label?:        string;
  /** Optional declared source table — boosts auto-resolution when a page knows it. */
  source_table?: string;
}

interface FlnState {
  reg:     Map<string, FlnFieldReg>;
  dirty:   Set<string>;
  session: AuthSession | null;
  isAdmin: boolean;
  timer:   ReturnType<typeof setTimeout> | null;
}

// Held on globalThis so a single registry survives Vite HMR instance splits.
const STORE_KEY = '__integrytas_fln_fields__';
function state(): FlnState {
  const g = globalThis as unknown as Record<string, unknown>;
  if (!g[STORE_KEY]) {
    g[STORE_KEY] = { reg: new Map(), dirty: new Set(), session: null, isAdmin: false, timer: null } as FlnState;
  }
  return g[STORE_KEY] as FlnState;
}

function regKey(r: FlnFieldReg): string {
  return `${r.page_code}|${r.component_key}|${r.field_key}`;
}

/** Declare that a field renders on a page (call from PersonalizedField/Grid). */
export function registerFlnField(r: FlnFieldReg): void {
  if (!r.page_code || !r.field_key) return;
  const s = state();
  const key = regKey(r);
  const prev = s.reg.get(key);
  if (prev && prev.label === r.label && prev.source_table === r.source_table) return;
  s.reg.set(key, r);
  s.dirty.add(key);
  scheduleFlush();
}

function scheduleFlush(): void {
  const s = state();
  if (!s.isAdmin || !s.session || s.timer || s.dirty.size === 0) return;
  // Debounce: let a freshly-opened page register its whole field set before pushing.
  s.timer = setTimeout(() => { s.timer = null; void flush(); }, 4000);
}

async function flush(): Promise<void> {
  const s = state();
  if (!s.isAdmin || s.dirty.size === 0) return;
  const batch = [...s.dirty].map(k => s.reg.get(k)).filter(Boolean) as FlnFieldReg[];
  s.dirty.clear();
  try {
    await apiDb.rpc('fln_sync_fields', { p_fields: batch });
  } catch {
    // Fail-open: re-queue so a later render/login retries the push.
    for (const r of batch) s.dirty.add(regKey(r));
  }
}

function isAdminSession(session: AuthSession | null): boolean {
  return !!session
    && session.effective_user.page_access.length > 0
    && session.effective_user.page_access.every(p => p.can_view && p.can_change);
}

/** Record the signed-in admin and flush any fields discovered before login. */
export function useFlnFieldSync(session: AuthSession | null): void {
  useEffect(() => {
    const s = state();
    s.session = session;
    s.isAdmin = isAdminSession(session);
    if (s.isAdmin) {
      // Re-queue everything already registered so the first admin session catches
      // fields that rendered before the admin flag was known.
      for (const k of s.reg.keys()) s.dirty.add(k);
      scheduleFlush();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.effective_user.user_id]);
}
